import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { downloadFile, siteRequest } from "lib/siteCustomerApi";
import { REFUND_RULE, useFirstAccessConfirm } from "components/ios/Alert";

export default function OrderDelivery({ orderId }) {
  const [state, setState] = useState({ loading: true }), [attempt, setAttempt] = useState(0);
  const [pending, setPending] = useState(null), [error, setError] = useState("");
  const download = useRef(null);
  const [confirmDialog, confirmFirst] = useFirstAccessConfirm();
  const confirming = useRef(false);
  const path = `/commerce/orders/${orderId}/delivery`;
  useEffect(() => {
    const ctrl = new AbortController(); setState({ loading: true }); setError("");
    siteRequest(path, { signal: ctrl.signal }).then(r => {
      if (ctrl.signal.aborted) return;
      const d = r.data;
      if (!["released", "unavailable", "sign_in_required"].includes(d?.status) || d.status === "released" && !Array.isArray(d.lines)) throw Error("Order files could not be verified. Refresh and try again.");
      setState({ data: d });
    }).catch(e => { if (!ctrl.signal.aborted) setState({ error: e.message }); });
    return () => { ctrl.abort(); download.current?.abort(); download.current = null; };
  }, [path, attempt]);
  async function start(file) {
    if (download.current) return;
    if (confirming.current) return;
    confirming.current = true;
    const ok = await confirmFirst(`order:${orderId}`).finally(() => { confirming.current = false; });
    if (!ok || download.current) return;
    const ctrl = new AbortController(); download.current = ctrl; setPending(file.id); setError("");
    try { await downloadFile({ id: orderId, file }, { signal: ctrl.signal, delivery: path }); }
    catch (e) { if (!ctrl.signal.aborted && e.name !== "AbortError") setError(e.message || "The transfer was interrupted. Try a fresh download."); }
    finally { if (download.current === ctrl) download.current = null; if (!ctrl.signal.aborted) setPending(null); }
  }
  const d = state.data;
  return <section aria-labelledby="order-files-title" className="store-order-bank">
    <h3 id="order-files-title">Prepared order files</h3>
    {state.loading ? <p role="status" className="store-checkout-note">Checking file availability…</p> : state.error ? <p role="alert" className="store-checkout-error">{state.error}</p> :
      d?.status === "released" ? <>
        <p className="store-checkout-note">Available in your account. Choose a file to start a protected download. {REFUND_RULE}</p>
        {d.lines.map(l => <div key={l.lineId}><h4>{l.item.name || l.item.packageId} · Quantity {l.item.quantity}</h4>
          <p className="store-checkout-note">{l.item.arrangement} · {l.item.equipmentLabel || l.item.equipment} · {l.item.stateLabel || l.item.state}</p>
          {l.files.map(f => <FlowButton key={f.id} type="button" className="store-checkout-button store-checkout-button-outline" disabled={!!pending} onClick={() => start(f)}>
            {pending === f.id ? "Downloading…" : `Download ${f.name}`}</FlowButton>)}</div>)}
      </> : d?.status === "sign_in_required" ? <p className="store-checkout-note">Private order files require your verified account. <Link to="/customer/access">Sign in with the email used at checkout</Link>, then return to this order.</p> :
        <p className="store-checkout-note">Files are not available for download yet. Preparation, payment review and account verification must be complete.</p>}
    {error && <p role="alert" className="store-checkout-error">{error}</p>}
    {confirmDialog}
    <FlowButton type="button" className="store-checkout-button store-checkout-button-outline" disabled={!!pending || state.loading} onClick={() => setAttempt(v => v + 1)}>Refresh file availability</FlowButton>
  </section>;
}
